// OWNER: FRONTEND-ADMIN. Pure validation for the admin screens (no DOM). The server enforces the same rules;
// these give instant, specific feedback and stop obviously unsafe saves before they are sent.
import type {
  CallingWindow, Campaign, CampaignScript, Country, ObjectionCategory, PreferredWindow, Role, StateRule,
} from '../../../src/shared/api';
import { OBJECTIONS } from '../../../src/shared/api';
import { looksHealthcare } from '../../../src/shared/api-core';
// CONTENT's house rules: CORE runs the same check on draft → live (routes/campaigns.ts goLiveProblems). Pure.
import { validateTemplate } from '../../../content/validate';

export const LEGAL_NAME = 'Valistix Inc.';

export interface Problem { level: 'error' | 'warn'; where: string; message: string }

// ---------- small format checks ----------

export const isHhmm = (s: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
/** http(s) link whose host has a dot, e.g. 'https://www.ftc.gov/x'. */
export const isHttpUrl = (s: string): boolean => /^https?:\/\/[^\s/?#.]+(\.[^\s/?#.]+)+([/?#]\S*)?$/i.test(s.trim());
export function isYmd(s: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}
export const wordCount = (s: string): number => (s.trim() ? s.trim().split(/\s+/).length : 0);

// ---------- campaign script ----------

export function emptyScript(): CampaignScript {
  return {
    summary: '', openers: [], reason: '', questions: [], pitch: '', ask: '', gatekeeper: [], objections: [],
    voicemailsLive: [], voicemailsDrop: [], emails: [], disqualifiers: [], complianceNotes: [],
  };
}

/** Fill any missing parts (a new campaign's script_json is '{}'), so the editor never meets undefined. */
export function normalizeScript(raw: Partial<CampaignScript> | null | undefined): CampaignScript {
  const s = emptyScript();
  if (!raw || typeof raw !== 'object') return s;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  return {
    summary: str(raw.summary), openers: arr(raw.openers), reason: str(raw.reason), questions: arr(raw.questions),
    pitch: str(raw.pitch), ask: str(raw.ask), gatekeeper: arr(raw.gatekeeper), objections: arr(raw.objections),
    voicemailsLive: arr(raw.voicemailsLive), voicemailsDrop: arr(raw.voicemailsDrop), emails: arr(raw.emails),
    disqualifiers: arr<string>(raw.disqualifiers), complianceNotes: arr<string>(raw.complianceNotes),
  };
}

const PHRASE_RULES: Array<{ re: RegExp; level: Problem['level']; message: string }> = [
  { re: /zero\s*-?\s*lossed/i, level: 'error', message: 'mentions ZeroLossed. Valistix scripts may not reference it or its clients.' },
  { re: /\b62\s?%|\b85\s?%|\b80\s?%|\$\s?126\s?k\b|\$\s?126,000/i, level: 'error', message: 'quotes a statistic the research refuted (62% / 85% / 80% / $126k). Remove it.' },
  { re: /\bguarantee(d|s)?\b/i, level: 'warn', message: 'says "guarantee". No results promises.' },
  { re: /\b(cure[sd]?|heal(s|ing)?|pain relief|relieves?|anxiety|insomnia|medical benefits?|therapeutic)\b/i, level: 'warn', message: 'may read as a health or cannabis-effect claim. Rephrase.' },
  { re: /\bbad time\b/i, level: 'warn', message: 'asks if it is a "bad time". Use a permission/context opener instead.' },
];

function phraseProblems(where: string, text: string, out: Problem[]): void {
  for (const r of PHRASE_RULES) if (r.re.test(text)) out.push({ level: r.level, where, message: `${where} ${r.message}` });
}

const DROP_ALLOWED_PLACEHOLDERS = new Set(['{setter}', '{callback}']);

/** Every problem in a campaign script. Errors block going live; warnings are shown in the editor.
 *  Pass the campaign's country to apply the Canada rule (no recorded drops, SPEC §1). */
export function scriptProblems(script: CampaignScript, country?: Country | 'MIXED'): Problem[] {
  const out: Problem[] = [];
  const err = (where: string, message: string) => out.push({ level: 'error', where, message });
  const warn = (where: string, message: string) => out.push({ level: 'warn', where, message });

  if (!script.summary.trim()) err('Summary', 'Add a one-line summary: who we call and why.');
  if (!script.openers.some((o) => o.text.trim())) err('Openers', 'Add at least one opener.');
  script.openers.forEach((o, i) => { if (!o.text.trim()) err(`Opener ${i + 1}`, `Opener ${i + 1} is empty. Fill it in or remove it.`); });
  if (!script.reason.trim()) err('Reason', 'Add the reason for the call (said in the first 15 seconds).');
  if (!script.pitch.trim()) err('Pitch', 'Add the pitch.');
  if (!script.ask.trim()) err('Ask', 'Add the meeting ask (two specific times, next day first).');
  if (script.questions.length === 0) warn('Questions', 'No problem questions yet.');
  script.questions.forEach((q, i) => { if (!q.text.trim()) err(`Question ${i + 1}`, `Question ${i + 1} is empty.`); });
  if (script.gatekeeper.length === 0) warn('Gatekeeper', 'No gatekeeper lines yet.');
  script.gatekeeper.forEach((g, i) => { if (!g.text.trim()) err(`Gatekeeper line ${i + 1}`, `Gatekeeper line ${i + 1} is empty.`); });

  if (script.objections.length === 0) warn('Objections', 'No objection cards yet (aim for 8–12).');
  const keys = new Set<string>();
  script.objections.forEach((o, i) => {
    const w = `Objection ${i + 1}${o.label ? ` (“${o.label}”)` : ''}`;
    if (!o.key.trim()) err(w, `${w} needs a key.`);
    else if (keys.has(o.key.trim())) err(w, `${w} repeats the key “${o.key}”. Keys must be unique.`);
    keys.add(o.key.trim());
    if (!o.label.trim()) err(w, `${w} needs a button label.`);
    if (o.label.length > 24) err(w, `${w}: the button label is ${o.label.length} characters; keep it to 24.`);
    if (!(OBJECTIONS as readonly string[]).includes(o.category as ObjectionCategory)) err(w, `${w} needs a category.`);
    if (!o.response.trim()) err(w, `${w} needs a response.`);
  });

  script.voicemailsLive.forEach((v, i) => {
    const w = `Live voicemail ${i + 1}${v.label ? ` (“${v.label}”)` : ''}`;
    if (!v.text.trim()) err(w, `${w} is empty.`);
    if (!v.situation.trim()) err(w, `${w} needs a situation (e.g. first, second).`);
    if (wordCount(v.text) > 65) warn(w, `${w} is ${wordCount(v.text)} words, longer than about 25 seconds spoken.`);
    if (v.text.trim() && !v.text.includes('{callback}')) warn(w, `${w} doesn’t say the callback number ({callback}).`);
  });

  if (country === 'CA' && script.voicemailsDrop.length) {
    err('Drop scripts', `${CANADA_NO_DROPS} Remove the ${script.voicemailsDrop.length} drop script${script.voicemailsDrop.length === 1 ? '' : 's'}: drops are never allowed to Canadian numbers.`);
  }
  const dropSituations = new Set<string>();
  script.voicemailsDrop.forEach((v, i) => {
    const w = `Drop script ${i + 1}${v.label ? ` (“${v.label}”)` : ''}`;
    if (!v.text.trim()) { err(w, `${w} is empty.`); return; }
    if (!v.situation.trim()) err(w, `${w} needs a situation (e.g. first, second).`);
    else if (dropSituations.has(v.situation)) warn(w, `${w} repeats the situation “${v.situation}”.`);
    dropSituations.add(v.situation);
    if (!v.text.includes('{callback}')) err(w, `${w} must state the callback number ({callback}); a drop without one isn't allowed.`);
    if (!/valistix inc/i.test(v.text)) err(w, `${w} must name ${LEGAL_NAME} (the legal name) at the start.`);
    else if (v.text.toLowerCase().indexOf('valistix inc') > 80) warn(w, `${w} names ${LEGAL_NAME} late; say it in the first sentence.`);
    const others = (v.text.match(/\{[a-z_]+\}/gi) ?? []).filter((p) => !DROP_ALLOWED_PLACEHOLDERS.has(p));
    if (others.length) err(w, `${w} uses ${[...new Set(others)].join(', ')}. A recorded drop is reused on every call, so only {setter} and {callback} can be filled.`);
    if (wordCount(v.text) > 70) warn(w, `${w} is ${wordCount(v.text)} words; recordings are capped at 30 seconds.`);
  });

  script.emails.forEach((e, i) => {
    const w = `Email ${i + 1}${e.situation ? ` (${e.situation})` : ''}`;
    if (!e.subject.trim()) err(w, `${w} needs a subject.`);
    if (!e.body.trim()) err(w, `${w} needs a body.`);
    else if (!e.body.includes('{mailing_address}')) err(w, `${w} needs {mailing_address} in the sign-off: CASL and CAN-SPAM require a postal address in every commercial email.`);
  });
  if (script.disqualifiers.length === 0) warn('Disqualifiers', 'No "not a fit" signals listed.');
  script.disqualifiers.forEach((d, i) => { if (!d.trim()) err(`Disqualifier ${i + 1}`, `Disqualifier ${i + 1} is empty.`); });
  if (script.complianceNotes.length === 0) warn('Compliance notes', 'No compliance notes; setters see these pinned on the dialer.');
  script.complianceNotes.forEach((d, i) => { if (!d.trim()) err(`Compliance note ${i + 1}`, `Compliance note ${i + 1} is empty.`); });

  // phrase rules across every text part
  phraseProblems('Summary', script.summary, out);
  script.openers.forEach((o, i) => phraseProblems(`Opener ${i + 1}`, o.text, out));
  phraseProblems('Reason', script.reason, out);
  script.questions.forEach((q, i) => phraseProblems(`Question ${i + 1}`, q.text, out));
  phraseProblems('Pitch', script.pitch, out);
  phraseProblems('Ask', script.ask, out);
  script.gatekeeper.forEach((g, i) => phraseProblems(`Gatekeeper line ${i + 1}`, g.text, out));
  script.objections.forEach((o, i) => phraseProblems(`Objection ${i + 1}`, `${o.response} ${o.followUpQuestion ?? ''}`, out));
  script.voicemailsLive.forEach((v, i) => phraseProblems(`Live voicemail ${i + 1}`, v.text, out));
  script.voicemailsDrop.forEach((v, i) => phraseProblems(`Drop script ${i + 1}`, v.text, out));
  script.emails.forEach((e, i) => phraseProblems(`Email ${i + 1}`, `${e.subject} ${e.body}`, out));
  return out;
}

/** CORE's go-live message (routes/campaigns.ts, fix round 1 finding 19): only active users with role 'setter' count. */
export const NO_ACTIVE_SETTER = "Assign at least one active setter before going live (admins don't count).";

/** Assigned users who count for go-live: active, role 'setter'. */
export function activeSetterIds(userIds: number[], users: ReadonlyArray<{ id: number; role: Role; active: boolean }>): number[] {
  return userIds.filter((id) => users.some((u) => u.id === id && u.active && u.role === 'setter'));
}

/** Why a campaign can't go live yet (empty = OK). SPEC §15.5: ≥ 1 active setter (admins don't count) and a script with
 *  no errors. Pass `users` (GET /api/users) to apply the active-setter rule; without it any assigned user counts. */
export function goLiveProblems(c: {
  script: CampaignScript; userIds: number[]; country?: Country | 'MIXED';
  users?: ReadonlyArray<{ id: number; role: Role; active: boolean }> | null;
}): string[] {
  const out = scriptProblems(c.script, c.country).filter((p) => p.level === 'error').map((p) => p.message);
  const setters = c.users ? activeSetterIds(c.userIds, c.users) : c.userIds;
  if (setters.length === 0) out.unshift(NO_ACTIVE_SETTER);
  return out;
}

/** The server's own go-live check: CONTENT's house rules on this campaign's script, windows, niche and offer, built the
 *  way CORE builds it (routes/campaigns.ts goLiveProblems), so the page lists them before the admin clicks Go live.
 *  Practice personas are left out there too (AI-TRAINING's certification covers them). */
export function houseRuleProblems(c: Pick<Campaign, 'id' | 'name' | 'niche' | 'offer' | 'country' | 'script' | 'windows' | 'templateKey'>): string[] {
  const key = c.templateKey && /^[a-z0-9]+(-[a-z0-9]+)*$/.test(c.templateKey) ? c.templateKey : `campaign-${c.id}`;
  return validateTemplate({
    key, name: c.name, niche: c.niche, offer: c.offer, country: c.country, script: normalizeScript(c.script), windows: c.windows ?? [], personas: [],
  })
    .filter((p) => !/persona/i.test(p))
    .map((p) => (p.startsWith(`${key} `) ? p.slice(key.length + 1) : p.startsWith(`${key}: `) ? p.slice(key.length + 2) : p));
}

/** SPEC §5.4: Canadian healthcare facilities can't be cold called (CRTC). The server's list and rule
 *  (api-core looksHealthcare; routes/campaigns.ts healthcareCampaignProblem): CA or MIXED and a matching niche. */
export function isBlockedHealthcareCampaign(niche: string, country: Country | 'MIXED'): boolean {
  return (country === 'CA' || country === 'MIXED') && looksHealthcare(niche);
}

export function preferredWindowProblems(w: PreferredWindow, idx: number): string[] {
  const out: string[] = [];
  const n = `Window ${idx + 1}${w.label ? ` (“${w.label}”)` : ''}`;
  if (!w.label.trim()) out.push(`${n} needs a label.`);
  if (w.days.length === 0) out.push(`${n} needs at least one day.`);
  if (w.days.some((d) => !Number.isInteger(d) || d < 1 || d > 6)) out.push(`${n}: days must be Monday–Saturday (Sunday is never dialled).`);
  if (!isHhmm(w.start) || !isHhmm(w.end)) out.push(`${n}: times must be HH:MM (24-hour).`);
  else if (w.start >= w.end) out.push(`${n}: the start must be before the end.`);
  // CONTENT house rule, checked again at go-live: a preferred window sits inside the default calling window
  else if (w.start < PREFERRED_WINDOW_BOUNDS[0] || w.end > PREFERRED_WINDOW_BOUNDS[1]) {
    out.push(`${n}: keep it inside ${PREFERRED_WINDOW_BOUNDS[0]}–${PREFERRED_WINDOW_BOUNDS[1]} (the default calling window); going live refuses a window outside it.`);
  }
  return out;
}

/** content/validate.ts: "preferred windows: inside the default calling window (09:00-20:00), never Sunday". */
export const PREFERRED_WINDOW_BOUNDS: readonly [string, string] = ['09:00', '20:00'];

// ---------- jurisdiction rules ----------

/** Decisions already made (SPEC §1): these never get calling turned on from the UI. */
export const LOCKED_OFF: Record<string, string> = {
  'US-TX': 'Texas is excluded (telemarketer registration under Tex. Bus. & Com. Code ch. 302). Calling stays off.',
  'CA-QC': 'Quebec is excluded. Calling stays off.',
};

type WindowBounds = { weekday: [string, string]; saturday: [string, string]; sunday: [string, string] | null };
/** Mirrors CORE src/worker/lib/settings.ts (tests/admin-fix2.test.ts checks the tables and the sentences match).
 *  Outer legal limits, prospect-local. US: TSR/TCPA 8 am-9 pm, every day. Canada: CRTC weekdays 9:00-21:30, weekends
 *  10:00-18:00; no Sunday calls in Canada (owner decision 2026-10-03, SPEC §19). */
export const LEGAL_BOUNDS: Record<Country, WindowBounds> = {
  US: { weekday: ['08:00', '21:00'], saturday: ['08:00', '21:00'], sunday: ['08:00', '21:00'] },
  CA: { weekday: ['09:00', '21:30'], saturday: ['10:00', '18:00'], sunday: null },
};
/** What an admin may set (CORE fix round 1): an hour inside the legal hours wherever a time-zone guess could be off. */
export const ADMIN_WINDOW_BOUNDS: Record<Country, WindowBounds> = {
  US: { weekday: ['09:00', '20:00'], saturday: ['09:00', '20:00'], sunday: ['09:00', '20:00'] },
  CA: { weekday: ['09:00', '20:30'], saturday: ['10:00', '17:00'], sunday: null },
};

/** CORE's validateCallingWindow checks and sentences, so the form says what the server would say.
 *  `where` is what the server puts first: '<state name> window' for a state rule. */
export function callingWindowProblems(w: CallingWindow | null, country: Country = 'US', where = 'Window'): string[] {
  if (!w) return [];
  const out: string[] = [];
  for (const part of ['weekday', 'saturday', 'sunday'] as const) {
    const b = w[part];
    if (!b) continue;
    const bounds = ADMIN_WINDOW_BOUNDS[country][part];
    if (!bounds) { out.push(`${where} sunday: Sunday calls aren't allowed in Canada. Leave Sunday off (null).`); continue; }
    if (!isHhmm(b.start) || !isHhmm(b.end)) { out.push(`${where} ${part}: times must be HH:MM (24-hour).`); continue; }
    if (b.end <= b.start) { out.push(`${where} ${part}: the end time must be after the start time.`); continue; }
    const [lo, hi] = bounds;
    if (b.start < lo || b.end > hi) {
      const [llo, lhi] = LEGAL_BOUNDS[country][part] ?? bounds;
      out.push(`${where} ${part}: ${country} law allows calls between ${llo} and ${lhi} local time, and the dialer keeps a margin for time-zone guesses, so the window must sit between ${lo} and ${hi}.`);
    }
  }
  return out;
}

/** 'Mon–Fri 09:00–20:30 · Sat 10:00–17:00 · no Sundays': what a country's windows may span (form hints). */
export function adminBoundsText(country: Country): string {
  const b = ADMIN_WINDOW_BOUNDS[country];
  return `Mon–Fri ${b.weekday[0]}–${b.weekday[1]} · Sat ${b.saturday[0]}–${b.saturday[1]} · ${b.sunday ? `Sun ${b.sunday[0]}–${b.sunday[1]}` : 'no Sundays'}`;
}

/** CORE rejects shorter review notes (routes/settings.ts). */
export const MIN_REVIEW_NOTE = 20;

/** The per-state conditions (migrations 0005 and 0006) may be left out of a draft: missing = unchanged. */
export type StateRuleDraft = Pick<StateRule, 'callingAllowed' | 'dropAllowed' | 'window' | 'registrationNote' | 'notes' | 'sources'>
  & Partial<Pick<StateRule, 'scrubAllLines' | 'businessLinesOnly' | 'setterNote' | 'handPickOnly'>>;

/** What each condition means, one line each (Settings → State rules). */
export const STATE_CONDITION_HELP = {
  scrubAllLines: 'Scrub every number here against the national Do Not Call list, landlines included (e.g. Georgia, Nevada, New York).',
  businessLinesOnly: 'Dial only numbers a lookup verified as a business landline or fixed VoIP; mobiles and unknown lines stay blocked (e.g. New Jersey, Tennessee).',
  setterNote: 'One or two lines the setter sees on the lead card before dialling: a recommended tip for calls into this state (since 2026-10-03, tips, not must-says).',
  handPickOnly: 'The dialer never picks these leads by itself; setters open each one from Leads (for laws that cover automated selection of numbers, e.g. Oklahoma).',
  noSunday: 'This state restricts telemarketing on Sundays, so its leads are never called on a Sunday, whatever the US Sunday hours say (migration 0021).',
} as const;

/** Longest setter note (CORE routes/settings.ts SETTER_NOTE_MAX); it sits on the lead card above the script. */
export const MAX_SETTER_NOTE = 400;

/** Trimmed, inner whitespace collapsed; empty = null (no note). */
export function normalizeSetterNote(text: string | null | undefined): string | null {
  const t = (text ?? '').replace(/\s+/g, ' ').trim();
  return t || null;
}

export function setterNoteProblems(text: string | null | undefined): Problem[] {
  const t = normalizeSetterNote(text);
  const where = 'Setter note';
  if (!t) return [];
  if (t.length > MAX_SETTER_NOTE) return [{ level: 'error', where, message: `The setter note is ${t.length} characters; keep it to ${MAX_SETTER_NOTE} (one or two lines).` }];
  const out: Problem[] = [];
  if (t.length > 200) out.push({ level: 'warn', where, message: `The setter note is ${t.length} characters; setters read it before every dial, so one or two short lines work best.` });
  if (/zero\s*-?\s*lossed/i.test(t)) out.push({ level: 'error', where, message: 'The setter note mentions ZeroLossed. Valistix calls may not reference it.' });
  return out;
}

/** What a draft relaxes compared with the saved rule. Each one needs a review note and a source, like opening a state.
 *  A condition only loosens anything while calling is on (CORE applies the same rule); removing the setter note is
 *  counted too (the server doesn't), because the note carries what the state requires the setter to say. */
export function stateRuleLoosening(before: StateRule, after: StateRuleDraft): string[] {
  const out: string[] = [];
  if (after.callingAllowed && !before.callingAllowed) out.push('turning calling on');
  if (after.dropAllowed && !before.dropAllowed) out.push('turning voicemail drops on');
  if (after.callingAllowed) {
    if (before.scrubAllLines && after.scrubAllLines === false) out.push('removing the all-lines Do Not Call scrub');
    if (before.businessLinesOnly && after.businessLinesOnly === false) out.push('allowing mobiles and unverified lines');
    if (normalizeSetterNote(before.setterNote) && after.setterNote !== undefined && !normalizeSetterNote(after.setterNote)) out.push('removing the setter note');
    if (before.handPickOnly && after.handPickOnly === false) out.push('letting the dialer pick these leads by itself');
  }
  return out;
}

const listText = (items: string[]): string => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

/** SPEC §15.9: opening a US state (calling or drops) or loosening one of its conditions needs a (new) note and at least
 *  one source URL, and saves as reviewed. Marking a US state reviewed needs a real note (not the seeded "Not reviewed
 *  yet") and a source URL too. */
export function stateRuleProblems(before: StateRule, after: StateRuleDraft, markReviewed = false): string[] {
  const out: string[] = [];
  const turningOnDrops = after.dropAllowed && !before.dropAllowed;
  const loosening = stateRuleLoosening(before, after);
  if (after.callingAllowed && LOCKED_OFF[before.code]) out.push(LOCKED_OFF[before.code]);
  if (after.dropAllowed && before.country === 'CA') out.push('Voicemail drops are never allowed to Canadian numbers.');
  if (after.dropAllowed && !after.callingAllowed) out.push('Turn calling on before allowing drops.');
  const badSources = after.sources.filter((s) => s.trim() && !isHttpUrl(s));
  if (badSources.length) out.push(`These sources aren't web links: ${badSources.join(', ')}`);
  if (before.country === 'US' && loosening.length) {
    const what = listText(loosening);
    const note = (after.notes ?? '').trim();
    if (!note) out.push(`Write a note saying what you checked before ${what}.`);
    else if (note === (before.notes ?? '').trim()) out.push(`Update the note to say what you checked (registration, state do-not-call list, ${turningOnDrops ? 'prerecorded-message law' : 'calling hours, line-type rules'}) before ${what}.`);
    else if (/not reviewed yet/i.test(note)) out.push('The note still says "Not reviewed yet". Replace it with what you checked.');
    else if (note.length < MIN_REVIEW_NOTE) out.push(`Write a fuller note (at least ${MIN_REVIEW_NOTE} characters) saying what you checked.`);
    if (!after.sources.some((s) => isHttpUrl(s))) out.push('Add at least one source URL (the statute or regulator page you checked).');
  } else if (markReviewed && before.country === 'US') {
    const note = (after.notes ?? '').trim();
    if (!note || /not reviewed yet/i.test(note) || note.length < MIN_REVIEW_NOTE) out.push(`Write what you checked in the notes (at least ${MIN_REVIEW_NOTE} characters) before marking the state reviewed.`);
    if (!after.sources.some((s) => isHttpUrl(s))) out.push('Add at least one source URL before marking the state reviewed.');
  }
  if (after.setterNote !== undefined) out.push(...setterNoteProblems(after.setterNote).filter((p) => p.level === 'error').map((p) => p.message));
  out.push(...callingWindowProblems(after.window, before.country, `${before.name} window`));
  return out;
}

export interface ConditionChip { key: 'scrubAllLines' | 'businessLinesOnly' | 'setterNote' | 'handPickOnly' | 'noSunday'; text: string; tone: 'info' | 'warn'; title: string }

/** The chips the State rules table shows for a rule's conditions (none when the server doesn't send the fields). */
export function stateConditionChips(r: Partial<Pick<StateRule, 'scrubAllLines' | 'businessLinesOnly' | 'setterNote' | 'handPickOnly' | 'noSunday'>>): ConditionChip[] {
  const out: ConditionChip[] = [];
  if (r.handPickOnly === true) out.push({ key: 'handPickOnly', text: 'hand-pick only', tone: 'warn', title: STATE_CONDITION_HELP.handPickOnly });
  if (r.noSunday === true) out.push({ key: 'noSunday', text: 'no Sunday calls', tone: 'info', title: STATE_CONDITION_HELP.noSunday });
  if (r.scrubAllLines === true) out.push({ key: 'scrubAllLines', text: 'DNC scrub: all lines', tone: 'info', title: STATE_CONDITION_HELP.scrubAllLines });
  if (r.businessLinesOnly === true) out.push({ key: 'businessLinesOnly', text: 'business lines only', tone: 'warn', title: STATE_CONDITION_HELP.businessLinesOnly });
  const note = normalizeSetterNote(r.setterNote);
  if (note) out.push({ key: 'setterNote', text: 'setter note', tone: 'info', title: `Setters see: “${note}”` });
  return out;
}

/** The condition fields the PATCH carried that the returned rule doesn't show (a server without migration 0005's
 *  columns ignores them). Empty = everything sent was saved. */
export function unsavedStateFields(
  sent: { scrubAllLines?: boolean; businessLinesOnly?: boolean; setterNote?: string | null; handPickOnly?: boolean },
  saved: Partial<Pick<StateRule, 'scrubAllLines' | 'businessLinesOnly' | 'setterNote' | 'handPickOnly'>>,
): string[] {
  const out: string[] = [];
  if (sent.handPickOnly !== undefined && saved.handPickOnly !== sent.handPickOnly) out.push('hand-pick only');
  if (sent.scrubAllLines !== undefined && saved.scrubAllLines !== sent.scrubAllLines) out.push('the all-lines DNC scrub');
  if (sent.businessLinesOnly !== undefined && saved.businessLinesOnly !== sent.businessLinesOnly) out.push('business lines only');
  if (sent.setterNote !== undefined && (saved.setterNote === undefined || normalizeSetterNote(saved.setterNote) !== normalizeSetterNote(sent.setterNote))) out.push('the setter note');
  return out;
}

// ---------- settings ----------

export function greetingProblems(text: string): Problem[] {
  const out: Problem[] = [];
  if (!text.trim()) return [{ level: 'error', where: 'Greeting', message: 'The greeting can’t be empty.' }];
  if (!/valistix inc/i.test(text)) out.push({ level: 'error', where: 'Greeting', message: `Say “${LEGAL_NAME}” so callers know who called them.` });
  if (!/\b9\b|\bnine\b/i.test(text)) out.push({ level: 'error', where: 'Greeting', message: 'Tell callers to press 9 to stop all calls; the opt-out listens for 9.' });
  if (text.trim().length < 20) out.push({ level: 'error', where: 'Greeting', message: 'Write a full sentence or two.' });
  if (text.length > 600) out.push({ level: 'error', where: 'Greeting', message: `The greeting is ${text.length} characters; the limit is 600.` });
  else if (text.length > 500) out.push({ level: 'warn', where: 'Greeting', message: `The greeting is ${text.length} characters; callers hang up on long greetings.` });
  return out;
}

/** CASL s.6(2)(c) and CAN-SPAM §7704(a)(5) both require a valid postal address in every commercial email. */
export const MAX_MAILING_ADDRESS = 300;
const US_ZIP = /\b\d{5}(?:-\d{4})?\b/;
const CA_POSTAL = /\b[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d\b/i;

/** Trim each line, collapse inner spaces, drop blank lines. This is what gets saved and put into {mailing_address}. */
export function normalizeMailingAddress(text: string): string {
  return text.split(/\r?\n/).map((l) => l.trim().replace(/\s+/g, ' ')).filter(Boolean).join('\n');
}

export function mailingAddressProblems(text: string): Problem[] {
  const a = normalizeMailingAddress(text);
  const where = 'Mailing address';
  if (!a) return [{ level: 'warn', where, message: 'Not set. Setters can’t send follow-up emails until it is: CASL and CAN-SPAM need a postal address in every email.' }];
  const out: Problem[] = [];
  if (a.length > MAX_MAILING_ADDRESS) out.push({ level: 'error', where, message: `The address is ${a.length} characters; the limit is ${MAX_MAILING_ADDRESS}.` });
  if (/https?:\/\/|www\.|@/i.test(a)) out.push({ level: 'error', where, message: 'Postal address only: no web or email address (the sign-off already has valistix.com).' });
  // same floor as CORE's validateMailingAddress (lib/settings.ts): ≥ 15 characters, a digit, ≥ 4 words
  if (!/\d/.test(a) || a.length < 15 || a.split(/[\s,]+/).filter(Boolean).length < 4) out.push({ level: 'error', where, message: 'Write the full postal address: street number (or PO box / suite), city, province or state, and postal or ZIP code.' });
  else if (!US_ZIP.test(a) && !CA_POSTAL.test(a)) out.push({ level: 'error', where, message: 'Add the postal code (A1A 1A1) or ZIP code (12345).' });
  if (/\bvalistix\b/i.test(a)) out.push({ level: 'warn', where, message: 'The sign-off already says “Valistix Inc.” on the line above; leave the company name out of the address.' });
  if (a.split('\n').length > 4) out.push({ level: 'warn', where, message: 'Keep it to four lines or fewer; it sits at the bottom of every email.' });
  return out;
}

// ---------- Calendly booking link (SPEC §10: meetings book into the campaign's calendly_event_slug) ----------

/** CampaignTemplate.calendlyEventSlug default (src/shared/api.ts) and the DB default (0001_init.sql). */
export const DEFAULT_CALENDLY_SLUG = 'your-result';
/** The Valistix account and the setter booking links: one registry for the panel, the server and this page
 *  (src/shared/calendly.ts). */
import { CALENDLY_ACCOUNT_URL, FIX_LIST_SLUG, SETTER_LINKS, bookingLinkName, campaignLinkProblem } from '../../../src/shared/calendly';
import { isCannabisNiche } from '../../../src/shared/offer-guards';
export { CALENDLY_ACCOUNT_URL, SETTER_LINKS };
/** Event links a first meeting from a call can go to (CALENDLY.md, APPROVAL-three-links 2026-10-10). `/plan` is the
 *  secret second-meeting event. The Book panel also offers the other non-cannabis setter link on every campaign. */
export const CALENDLY_EVENTS: ReadonlyArray<{ slug: string; name: string; note: string }> = [
  { slug: 'your-result', name: 'Your missed-call fix', note: 'setter link: a yes to getting their calls answered; 2-hour minimum notice' },
  { slug: 'growth-call', name: 'Your growth call', note: 'setter link: everything else, or not sure (secret); 2-hour minimum notice' },
  { slug: 'your-store', name: "Your store's growth call", note: 'dispensaries only (secret setter link); 2-hour minimum notice' },
  { slug: FIX_LIST_SLUG, name: bookingLinkName(FIX_LIST_SLUG), note: 'the website link; 4-hour minimum notice' },
];

/** The slug for a template (its own, else the default). */
export function templateCalendlySlug(t: { calendlyEventSlug?: string } | null | undefined): string {
  return t?.calendlyEventSlug?.trim() || DEFAULT_CALENDLY_SLUG;
}

/** Accept a pasted link (any account handle): 'https://calendly.com/valistix/fix-list?month=2026-10' → 'fix-list'. */
export function normalizeCalendlySlug(input: string): string {
  const s = input.trim();
  const m = /^(?:https?:\/\/)?(?:www\.)?calendly\.com\/([^?#\s]*)/i.exec(s);
  if (m) {
    const parts = m[1].split('/').filter(Boolean);
    return parts.length >= 2 ? parts[1] : ''; // just the account link has no event in it
  }
  return s.replace(/^\/+|\/+$/g, '');
}

/** Same pattern and length as the server (routes/campaigns.ts), plus warnings for events setters shouldn't book into.
 *  With the campaign's niche (APPROVAL-three-links 6.3, the strict cannabis lock): a cannabis campaign on any link but
 *  your-store, or any other campaign on your-store, is an error that blocks saving. */
export function calendlySlugProblems(slug: string, niche?: string | null): Problem[] {
  const s = slug.trim();
  const where = 'Calendly event';
  if (!s) return [{ level: 'error', where, message: `Name the Calendly event setters book into, e.g. ${DEFAULT_CALENDLY_SLUG} (paste the link or type the last part).` }];
  if (s.length > 80 || !/^[a-z0-9][a-z0-9\-_]*$/i.test(s)) return [{ level: 'error', where, message: 'The Calendly slug is the last part of the link: letters, numbers and dashes, e.g. your-result.' }];
  if (niche !== undefined && niche !== null && niche.trim()) {
    // The same check the server makes on create and PATCH (routes/campaigns.ts).
    const lock = campaignLinkProblem(s, isCannabisNiche(niche));
    if (lock) return [{ level: 'error', where, message: lock }];
  }
  if (s.toLowerCase() === 'plan') return [{ level: 'warn', where, message: `/plan is the secret second-meeting event. First meetings from calls go to ${SETTER_LINKS.map((l) => l.slug).join(', ').replace(/, ([^,]*)$/, ' or $1')}.` }];
  if (!CALENDLY_EVENTS.some((e) => e.slug === s)) {
    return [{ level: 'warn', where, message: `“${s}” isn't one of the Valistix booking links (${CALENDLY_EVENTS.map((e) => e.slug).join(', ')}). Check that ${CALENDLY_ACCOUNT_URL}/${s} opens, or bookings from the dialer will fail.` }];
  }
  return [];
}

// ---------- recording scripts ----------

/** SPEC §1: never a recorded drop to any Canadian number. Shown instead of the recorder (CONTENT convention 5). */
export const CANADA_NO_DROPS = 'Canada: no recorded drops; read every voicemail live.';

/** Why a campaign gets no voicemail-drop recorder (null = setters can record for it). */
export function dropRecorderBlocker(c: { country: Country | 'MIXED'; script: Partial<CampaignScript> | null | undefined }): string | null {
  if (c.country === 'CA') return CANADA_NO_DROPS;
  if (normalizeScript(c.script).voicemailsDrop.length === 0) return 'This campaign has no recorded drops; read every voicemail live.';
  return null;
}

/** The built-in suggestion for a general voicemail ("Any campaign", PJ 2026-10-03) when none of the setter's campaigns
 *  has drop scripts: name and "Valistix" first, no claims, the callback number twice. Filled with fillDropScript. */
export const GENERAL_DROP_SCRIPT: CampaignScript['voicemailsDrop'][number] = {
  situation: 'first',
  label: 'General voicemail',
  text: "Hi, this is {setter} with Valistix. I'm calling about the calls your shop misses while you're busy or closed. Call me back at {callback}. Again, {callback}.",
};

/** The scripts the recorder suggests for "Any campaign (general voicemail)" (saved with campaignId null): the first
 *  campaign that allows drops lends its drop scripts; with none, the built-in general one. `from` names that campaign. */
export function anyCampaignDropScripts<C extends { name: string; country: Country | 'MIXED'; script: Partial<CampaignScript> | null | undefined }>(
  campaigns: C[],
): { scripts: CampaignScript['voicemailsDrop']; from: C | null } {
  const from = campaigns.find((c) => dropRecorderBlocker(c) === null) ?? null;
  return from ? { scripts: normalizeScript(from.script).voicemailsDrop, from } : { scripts: [GENERAL_DROP_SCRIPT], from: null };
}

/** Fill {setter} and {callback} in a drop script; report any placeholder left unfilled. */
export function fillDropScript(text: string, vars: { setter: string; callback: string | null }): { text: string; unfilled: string[] } {
  let out = text.replace(/\{setter\}/g, vars.setter);
  if (vars.callback) out = out.replace(/\{callback\}/g, vars.callback);
  const unfilled = [...new Set(out.match(/\{[a-z_]+\}/gi) ?? [])];
  return { text: out, unfilled };
}

// ---------- recording review (TELEPHONY routes/recordings.ts) ----------

/** The server refuses to approve a recording whose length was never measured (TELEPHONY fix round 1), and the drop
 *  route refuses to play one. The only way forward is Reject, so the setter records it again. */
export const UNMEASURED_RECORDING = "This recording's length was never measured, so it can't be approved or dropped. Reject it so the setter records it again.";
export const UNMEASURED_REJECT_NOTE = 'The length of this recording could not be measured. Please record it again.';

/** Why Approve is off for a recording (empty = the checklist decides). */
export function recordingApproveBlockers(r: { durationSec: number | null; humanVoiceAttested: boolean }, maxSeconds: number): string[] {
  const out: string[] = [];
  if (!r.humanVoiceAttested) out.push('The setter did not attest this is their own voice. Reject it.');
  if (r.durationSec === null) out.push(UNMEASURED_RECORDING);
  else if (r.durationSec > maxSeconds) out.push(`Longer than ${maxSeconds} s (${r.durationSec.toFixed(1)} s). Reject it.`);
  return out;
}

/** A recording that should be rejected now even outside the review queue: approved (or pending) with no measured length. */
export function needsRejectForLength(r: { durationSec: number | null; status: 'pending' | 'approved' | 'rejected' }): boolean {
  return r.durationSec === null && r.status !== 'rejected';
}

export function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? '';
}
